import React, { useState, useEffect } from 'react';
import { Download, Filter } from 'lucide-react';
import { admissionService } from '../../lib/services.ipd';
import type { Admission } from '../../lib/services.ipd';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';

export default function DischargedList() {
  const [admissions, setAdmissions] = useState<Admission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadDischargedPatients();
  }, []);

  const loadDischargedPatients = async () => {
    try {
      const { data, error } = await supabase
        .from('admissions')
        .select(`
          *,
          patient:patients(id, name, email, phone),
          doctor:doctors(id, name, specialization)
        `)
        .eq('status', 'discharged')
        .order('discharge_date', { ascending: false });
      
      if (error) throw error;
      setAdmissions(data || []);
    } catch (error) {
      console.error('Failed to load discharged patients:', error);
      toast.error('Failed to load discharged patients');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const csvData = filteredAdmissions.map((adm) => ({
      'Admission No': adm.admission_no,
      'Patient Name': adm.patient?.name,
      'Admission Date': new Date(adm.admission_date).toLocaleDateString(),
      'Discharge Date': adm.discharge_date ? new Date(adm.discharge_date).toLocaleDateString() : '',
      'Doctor': adm.doctor?.name,
      'Diagnosis': adm.provisional_diagnosis,
    }));
    
    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Discharged Patients');
    XLSX.writeFile(wb, `discharged-patients-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const filteredAdmissions = admissions.filter((adm) => {
    if (filters.startDate && adm.discharge_date && new Date(adm.discharge_date) < new Date(filters.startDate)) return false;
    if (filters.endDate && adm.discharge_date && new Date(adm.discharge_date) > new Date(filters.endDate)) return false;
    return true;
  });

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discharged Patients</h1>
          <p className="text-gray-600">View and export discharged patient records</p>
        </div>
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          <Download className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doctor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discharge Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diagnosis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAdmissions.map((adm) => (
              <tr key={adm.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {adm.admission_no}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {adm.patient?.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {adm.doctor?.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {new Date(adm.admission_date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {adm.discharge_date ? new Date(adm.discharge_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {adm.provisional_diagnosis || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredAdmissions.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No discharged patients found
          </div>
        )}
      </div>
    </div>
  );
}
